<script>
import {
  ref, computed, onMounted, onUnmounted,
} from 'vue';
import { mapActions, mapState, useStore } from 'vuex';
import Navigation from '@/components/ux/navigation/Navigation-block.vue';
import { useTheme } from '@/composables/useTheme';

export default {
  components: {
    Navigation,
  },
  setup() {
    const { initTheme } = useTheme();

    /**
     * Track sidebar collapsed state from localStorage so the main content
     * area can offset its margin to match the sidebar width. A storage
     * listener keeps this in sync when the Navigation component toggles it.
     */
    const sidebarCollapsed = ref(localStorage.getItem('sidebar-collapsed') === 'true');

    function onStorageChange(e) {
      if (e.key === 'sidebar-collapsed') {
        sidebarCollapsed.value = e.newValue === 'true';
      }
    }

    // Storage events only fire cross-tab; for same-tab sync we also
    // poll on a short interval — cheap because it is a single key read.
    let pollInterval = null;
    onMounted(() => {
      window.addEventListener('storage', onStorageChange);
      pollInterval = setInterval(() => {
        const current = localStorage.getItem('sidebar-collapsed') === 'true';
        if (current !== sidebarCollapsed.value) {
          sidebarCollapsed.value = current;
        }
      }, 200);
    });
    onUnmounted(() => {
      window.removeEventListener('storage', onStorageChange);
      clearInterval(pollInterval);
    });

    const store = useStore();
    const isCompanyUser = computed(() => store.getters['auth/isCompanyUser']);

    return { initTheme, sidebarCollapsed, isCompanyUser };
  },
  mounted() {
    this.checkAuth();
    // Инициализируем тему при загрузке приложения
    this.initTheme();
  },
  methods: {
    ...mapActions({
      checkAuth: 'auth/checkAuthorization',
    }),
    redirectToLogin() {
      if (!this.token) {
        window.location.href = '/';
      }
    },
  },
  computed: {
    ...mapState({
      token: (state) => state.auth.idToken,
    }),
  },
  watch: {
    token(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.redirectToLogin();
      }
    },
  },
};
</script>

<template>
  <!-- Login page: no navigation -->
  <template v-if="$route.path === '/'">
    <router-view />
  </template>

  <!-- All authenticated users: sidebar layout (menu items differ for company users) -->
  <a-layout v-else :style="{ minHeight: '100vh' }">
    <Navigation />
    <a-layout
      :style="{
        marginLeft: sidebarCollapsed ? '64px' : '240px',
        transition: 'margin-left 0.2s',
        overflow: 'auto',
      }"
    >
      <a-layout-content :style="{ minWidth: 0 }">
        <router-view />
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>

<style lang="scss">
// Импортируем систему тем
@import '@/styles/themes.scss';
@import '@/styles/modals.scss';

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

@import url('https://fonts.googleapis.com/css2?family=Alex+Brush&family=Allura&family=Bebas+Neue&family=Bungee+Shade&family=Caveat:wght@400..700&family=Dancing+Script:wght@400..700&family=EB+Garamond:ital,wght@0,400..800;1,400..800&family=Great+Vibes&family=IM+Fell+English+SC&family=Kalam:wght@300;400;700&family=La+Belle+Aurore&family=Lavishly+Yours&family=Life+Savers:wght@400;700;800&family=Lobster&family=Merriweather:ital,wght@0,300;0,400;0,700;0,900;1,300;1,400;1,700;1,900&family=Molle&family=Monoton&family=Montez&family=Pacifico&family=Sacramento&family=Satisfy&family=Shadows+Into+Light&family=Shrikhand&family=Spicy+Rice&family=Yellowtail&family=Yesteryear&family=Montserrat:ital,wght@0,100..900;1,100..900');
@import url('./styles/fonts/stylesheetOthers.css');

@font-face {
  font-family: "Recoleta";
  src: local("Recoleta"),
  url(./styles/fonts/Recoleta/Recoleta-Regular.woff2) format("woff2");
}
@font-face {
  font-family: "Egptienne_Bold";
  src: local("Egptienne_Bold"),
  url(./styles/fonts/egyptienne-mn/EgyptienneMNCondensed-Bold.woff2) format("woff2");
}
@font-face {
  font-family: "Futura_Book";
  src: local("Futura_Book"),
  url(./styles/fonts/Futura-Book/FuturaBT-Book.woff2) format("woff2");
}
@font-face {
  font-family: "Railway_Light";
  src: local("Railway_Light"),
  url(./styles/fonts/Raleway/Raleway-Light.woff2) format("woff2");
}
@font-face {
  font-family: "Pier_Sans_Bold";
  src: local("Pier_Sans_Bold"),
  url(./styles/fonts/Pier_Sans_Bold/PierSans-Bold.woff2) format("woff2");
}
@font-face {
  font-family: "Market_Fresh";
  src: local("Market_Fresh"),
  url(./styles/fonts/Market_Fresh/MarketFresh.woff2) format("woff2");
}
@font-face {
  font-family: "Bodoni_Medium";
  src: local("Bodoni_Medium"),
  url(./styles/fonts/Bodoni_Moda/BodoniModa-Regular.woff2) format("woff2");
}
@font-face {
  font-family: "Libre_Baskerville_Regular";
  src: local("Libre_Baskerville_Regular"),
  url(./styles/fonts/Libre_Baskerville/LibreBaskerville-Regular.woff2) format("woff2");
}
@font-face {
  font-family: "Neutraface_Text_Book";
  src: local("Neutraface_Text_Book"),
  url(./styles/fonts/Neutraface-Text-Book/NeutraText-Book.woff2) format("woff2");
}
@font-face {
  font-family: "Work Sans";
  src: local("Work Sans"),
  url(./styles/fonts/WorkSans/WorkSans-Regular.woff2) format("woff2");
}
@font-face {
  font-family: "ahmedy_webfont";
  src: local("ahmedy_webfont"),
  url(./styles/fonts/ahmedy_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "dark_moon_webfont";
  src: local("dark_moon_webfont"),
  url(./styles/fonts/dark_moon_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "elegant_webfont";
  src: local("elegant_webfont"),
  url(./styles/fonts/elegant_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "ethereal_medium_webfont";
  src: local("ethereal_medium_webfont"),
  url(./styles/fonts/ethereal_medium_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "mango_dream_webfont";
  src: local("mango_dream_webfont"),
  url(./styles/fonts/mango_dream_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "mindstay_webfont";
  src: local("mindstay_webfont"),
  url(./styles/fonts/mindstay_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "MontrealLight";
  src: local("MontrealLight"),
  url(./styles/fonts/MontrealLight.woff2) format("woff2");
}
@font-face {
  font-family: "romantic_history_webfont";
  src: local("romantic_history_webfont"),
  url(./styles/fonts/romantic_history_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "romantica_webfont";
  src: local("romantica_webfont"),
  url(./styles/fonts/romantica_webfont.woff2) format("woff2");
}
@font-face {
  font-family: "vintage_grunge_regular_webfont";
  src: local("vintage_grunge_regular_webfont"),
  url(./styles/fonts/vintage_grunge_regular_webfont.woff2) format("woff2");
}

@font-face {
  font-family: "Inter_Regular";
  src: local("Inter"),
  url(./styles/fonts/Inter/Inter-Regular.woff2) format("woff2");
}
@font-face {
  font-family: "Inter_Medium";
  src: local("Inter"),
  url(./styles/fonts/Inter/Inter-Medium.woff2) format("woff2");
}

</style>
